import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Filter state shared by every analytics screen. It lives in the URL so switching between
// screens (overview -> line history -> losses) keeps the same period, line and shift.

export type Preset = 'today' | 'yesterday' | 'this_week' | 'prev_week' | 'this_month' | 'custom';
export const PRESETS: Preset[] = ['today', 'yesterday', 'this_week', 'prev_week', 'this_month', 'custom'];

export interface AnalyticsFilters {
  preset: Preset;
  /** Local calendar dates (YYYY-MM-DD), inclusive; used only for `custom`. */
  customFrom: string;
  customTo: string;
  lineId: string;      // '' = all, otherwise a single id
  materialId: string;  // '' = all, '0' = unrecognised
  shift: '' | 'zmiana_1' | 'zmiana_2';
  compare: boolean;
}

const toLocalDate = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const startOfDay = (d: Date): Date => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number): Date => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d: Date): Date => { const x = startOfDay(d); const dow = (x.getDay() + 6) % 7; return addDays(x, -dow); };

/** [from, to) as local Date objects for a preset; `to` is exclusive (start of the next day). */
export const rangeFor = (f: AnalyticsFilters, now = new Date()): { from: Date; to: Date } => {
  const today = startOfDay(now);
  switch (f.preset) {
    case 'today': return { from: today, to: addDays(today, 1) };
    case 'yesterday': return { from: addDays(today, -1), to: today };
    case 'this_week': return { from: startOfWeek(today), to: addDays(today, 1) };
    case 'prev_week': { const s = addDays(startOfWeek(today), -7); return { from: s, to: addDays(s, 7) }; }
    case 'this_month': { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { from: s, to: addDays(today, 1) }; }
    case 'custom': {
      const from = f.customFrom ? startOfDay(new Date(f.customFrom)) : addDays(today, -6);
      const to = f.customTo ? addDays(startOfDay(new Date(f.customTo)), 1) : addDays(today, 1);
      return { from, to: to > from ? to : addDays(from, 1) };
    }
    default: return { from: today, to: addDays(today, 1) };
  }
};

/** Query string for the backend — ISO timestamps carry the local offset, so the server gets exact instants. */
export const toQuery = (f: AnalyticsFilters): Record<string, string> => {
  const { from, to } = rangeFor(f);
  const q: Record<string, string> = { from: isoWithOffset(from), to: isoWithOffset(to), compare: f.compare ? '1' : '0' };
  if (f.lineId) q.line_id = f.lineId;
  if (f.materialId) q.material_id = f.materialId;
  if (f.shift) q.shift = f.shift;
  return q;
};

const isoWithOffset = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  return `${toLocalDate(d)}T${p(d.getHours())}:${p(d.getMinutes())}:00${sign}${p(Math.floor(Math.abs(off) / 60))}:${p(Math.abs(off) % 60)}`;
};

export const rangeLabel = (f: AnalyticsFilters): string => {
  const { from, to } = rangeFor(f);
  const last = addDays(to, -1);
  const fmt = (d: Date) => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
  return toLocalDate(from) === toLocalDate(last) ? fmt(from) : `${fmt(from)} – ${fmt(last)}`;
};

export const useAnalyticsFilters = (): [AnalyticsFilters, (patch: Partial<AnalyticsFilters>) => void] => {
  const [params, setParams] = useSearchParams();
  const filters = useMemo<AnalyticsFilters>(() => ({
    preset: (PRESETS.includes(params.get('p') as Preset) ? params.get('p') : 'this_week') as Preset,
    customFrom: params.get('from') ?? '',
    customTo: params.get('to') ?? '',
    lineId: params.get('line') ?? '',
    materialId: params.get('material') ?? '',
    shift: (params.get('shift') as AnalyticsFilters['shift']) ?? '',
    compare: params.get('cmp') !== '0',
  }), [params]);

  const update = useCallback((patch: Partial<AnalyticsFilters>) => {
    const next = { ...filters, ...patch };
    const p = new URLSearchParams();
    if (next.preset !== 'this_week') p.set('p', next.preset);
    if (next.preset === 'custom') { if (next.customFrom) p.set('from', next.customFrom); if (next.customTo) p.set('to', next.customTo); }
    if (next.lineId) p.set('line', next.lineId);
    if (next.materialId) p.set('material', next.materialId);
    if (next.shift) p.set('shift', next.shift);
    if (!next.compare) p.set('cmp', '0');
    setParams(p, { replace: true });
  }, [filters, setParams]);

  return [filters, update];
};

/** Keeps the current filters when linking to another analytics screen. */
export const withFilters = (path: string, params: URLSearchParams): string => {
  const s = params.toString();
  return s ? `${path}?${s}` : path;
};
